class R13MobileStickyAtc extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;

    this.initialized = true;
    this.sectionId = this.dataset.sectionId;
    this.productFormId = this.dataset.productFormId;
    this.submitButtonId = this.dataset.submitButtonId;
    this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
    this.sizeOptionPosition = Number(this.dataset.sizeOptionPosition || 0);
    this.selectedSize = this.dataset.selectedSize || '';
    this.product = this.parseJson('[data-r13-sticky-product]');
    this.productInfo = this.closest('product-info');
    this.productForm = document.getElementById(this.productFormId);
    this.variantPicker = document.getElementById(`variant-selects-${this.sectionId}`);
    this.mainSubmitButton = document.getElementById(this.submitButtonId);
    this.sizeToggle = this.querySelector('[data-r13-sticky-size-toggle]');
    this.sizePanel = this.querySelector('[data-r13-sticky-sizes]');
    this.sizeText = this.querySelector('[data-r13-sticky-size]');
    this.submitButton = this.querySelector('[data-r13-sticky-submit]');
    this.submitText = this.querySelector('[data-r13-sticky-submit-text]');
    this.priceText = this.querySelector('[data-r13-sticky-price]');
    this.priceSeparator = this.querySelector('[data-r13-sticky-price-separator]');
    this.variantIdInput = this.productForm?.querySelector('input[name="id"]');
    this.footer = this.getFooter();
    this.footerIsVisible = false;
    this.sizePickerOpen = false;
    this.hasPickedSizeFromTray = false;
    this.cartPreferredSize = '';
    this.selectedSizeSource = '';
    this.sizePickerOpenScrollY = 0;
    this.isAdding = false;
    this.dismissedAfterAdd = false;
    this.selectedVariantAvailable = true;
    this.skipNextMainSubmitClick = false;
    this.boundMainSubmitClick = this.handleMainSubmitClick.bind(this);
    this.boundMobileFocusButtonClick = this.handleMobileFocusButtonClick.bind(this);
    this.boundCartDrawerMutation = this.handleCartDrawerMutation.bind(this);

    this.sizeToggle?.addEventListener('click', (event) => {
      event.preventDefault();
      this.toggleSizePanel();
    });
    this.submitButton?.addEventListener('click', (event) => {
      event.preventDefault();
      if (this.shouldUseMobileSizePicker() && !this.hasPickedSizeFromTray) {
        this.openSizePicker();
        return;
      }

      this.submitMainForm({ bypassPicker: true });
    });
    this.querySelectorAll('[data-r13-sticky-size-option]').forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        this.selectSize(button);
      });
    });
    this.mainSubmitButton?.addEventListener('click', this.boundMainSubmitClick, true);
    document.addEventListener('click', this.boundMobileFocusButtonClick, true);

    if (window.PUB_SUB_EVENTS && typeof window.subscribe === 'function') {
      this.variantUnsubscriber = window.subscribe(window.PUB_SUB_EVENTS.variantChange, ({ data }) => {
        if (data?.sectionId !== this.sectionId || !data.variant) return;
        this.updateFromVariant(data.variant, { syncSize: this.hasPickedSizeFromTray });
      });

      this.cartUpdateUnsubscriber = window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, (event) => {
        this.handleCartUpdate(event);
      });
    }

    this.observeMainSubmitButton();
    this.observeCartDrawer();
    this.setupVisibilityTracking();
    this.updateFromCurrentForm();
    this.syncPreferredSizeFromCart();
  }

  disconnectedCallback() {
    this.variantUnsubscriber?.();
    this.cartUpdateUnsubscriber?.();
    this.formObserver?.disconnect();
    this.footerObserver?.disconnect();
    this.submitObserver?.disconnect();
    this.cartDrawerObserver?.disconnect();
    this.clearScrollRestores();
    document.documentElement.classList.remove('r13-mobile-sticky-atc-is-visible');
    document.documentElement.classList.remove('r13-mobile-size-picker-is-open');
    this.hasPickedSizeFromTray = false;
    window.removeEventListener('scroll', this.visibilityHandler);
    window.removeEventListener('resize', this.visibilityHandler);
    this.mainSubmitButton?.removeEventListener('click', this.boundMainSubmitClick, true);
    document.removeEventListener('click', this.boundMobileFocusButtonClick, true);
  }

  parseJson(selector) {
    try {
      const node = this.querySelector(selector);
      return node ? JSON.parse(node.textContent) : null;
    } catch (error) {
      console.warn('Unable to parse sticky product data', error);
      return null;
    }
  }

  toggleSizePanel() {
    if (!this.sizePanel) return;

    const isOpening = this.sizePanel.hasAttribute('hidden');
    if (isOpening) {
      this.openSizePicker();
    } else {
      this.closeSizePicker({ skipVisibilityUpdate: true });
    }
  }

  selectSize(button) {
    if (!button || button.getAttribute('aria-disabled') === 'true') return;

    const size = button.dataset.r13StickySizeOption;
    const input = this.findMainSizeControl(size);
    const preserveScrollY = this.classList.contains('is-visible') ? window.scrollY : null;

    if (preserveScrollY !== null) {
      this.lockVariantPageScroll();
    }

    if (input) {
      if (input.tagName === 'SELECT') {
        input.value = size;
      } else {
        input.checked = true;
      }

      input.dispatchEvent(new Event('change', { bubbles: true }));
    }

    this.selectedSize = size;
    this.selectedSizeSource = 'customer';
    this.cartPreferredSize = size;
    this.hasPickedSizeFromTray = true;
    const variant = this.findVariantForSize(size);
    if (variant) {
      this.dataset.selectedVariantId = variant.id;
      this.selectedVariantAvailable = Boolean(variant.available);
      if (this.variantIdInput) {
        this.variantIdInput.value = variant.id;
        this.variantIdInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
      this.updatePrice(variant.price);
      this.updateAvailability(variant.available);
    }
    this.updateSelectedSize();

    if (preserveScrollY !== null) {
      this.preserveScrollPosition(preserveScrollY);
    }
    this.classList.add('is-visible', 'is-picker-open');
    this.sizePanel?.removeAttribute('hidden');
    document.documentElement.classList.add('r13-mobile-sticky-atc-is-visible', 'r13-mobile-size-picker-is-open');
    this.updateButtonStateFromMainButton();
  }

  findVariantForSize(size) {
    if (!this.product?.variants?.length || !this.sizeOptionPosition) return null;

    const currentVariantId = this.variantIdInput?.value || this.dataset.selectedVariantId;
    const currentVariant = this.product.variants.find((variant) => String(variant.id) === String(currentVariantId));
    const targetOptions = currentVariant?.options ? [...currentVariant.options] : [];
    targetOptions[this.sizeOptionPosition - 1] = size;

    return this.product.variants.find((variant) => (
      variant.options?.every((option, index) => option === targetOptions[index])
    )) || this.product.variants.find((variant) => variant[`option${this.sizeOptionPosition}`] === size);
  }

  findMainSizeControl(size) {
    this.variantPicker = document.getElementById(`variant-selects-${this.sectionId}`);
    if (!this.variantPicker) return null;

    const matchingRadio = Array.from(this.variantPicker.querySelectorAll('input[type="radio"]')).find((input) => {
      const optionName = String(input.dataset.optionName || '').toLowerCase();
      return input.value === size && optionName.includes('size');
    });
    if (matchingRadio) return matchingRadio;

    return Array.from(this.variantPicker.querySelectorAll('select')).find((select) => {
      const selectedOption = select.querySelector(`option[value="${this.cssEscape(size)}"]`);
      const optionName = String(selectedOption?.dataset.optionName || select.name || '').toLowerCase();
      return Boolean(selectedOption) && optionName.includes('size');
    });
  }

  submitMainForm(options = {}) {
    if (this.isAdding) return;

    if (this.shouldUseMobileSizePicker() && !options.bypassPicker) {
      this.openSizePicker();
      return;
    }

    if (this.shouldUseMobileSizePicker()) {
      this.addSelectedVariantToCart();
      return;
    }

    if (!this.mainSubmitButton || this.mainSubmitButton.disabled || this.mainSubmitButton.getAttribute('aria-disabled') === 'true') {
      return;
    }

    this.skipNextMainSubmitClick = true;
    this.mainSubmitButton.click();
  }

  addSelectedVariantToCart() {
    const variantId = this.variantIdInput?.value || this.dataset.selectedVariantId;
    if (!variantId) return;

    const quantityInput = this.productForm?.querySelector('input[name="quantity"]');
    const quantity = Math.max(1, parseInt(quantityInput?.value, 10) || 1);
    const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
    const formData = new FormData();

    formData.append('id', variantId);
    formData.append('quantity', quantity);

    if (cart?.getSectionsToRender) {
      formData.append('sections', cart.getSectionsToRender().map((section) => section.id));
      formData.append('sections_url', window.location.pathname);
      cart.setActiveElement?.(document.activeElement);
    }

    this.setAddingState(true);

    const config = typeof fetchConfig === 'function'
      ? fetchConfig('javascript')
      : {
          method: 'POST',
          headers: { Accept: 'application/javascript' },
        };

    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];
    config.body = formData;

    fetch(window.routes.cart_add_url, config)
      .then((response) => response.json())
      .then((response) => {
        if (response.status) throw new Error(response.description || response.message || 'Unable to add item');

        if (window.PUB_SUB_EVENTS && typeof window.publish === 'function') {
          window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
            source: 'r13-mobile-sticky-atc',
            productVariantId: variantId,
            cartData: response,
          });
        }

        if (cart?.classList) cart.classList.remove('is-empty');

        if (cart?.renderContents && response.sections) {
          cart.renderContents(response);
        } else {
          window.location.href = window.routes.cart_url;
        }

        this.dismissedAfterAdd = true;
        this.closeSizePicker({ skipVisibilityUpdate: true });
        this.classList.remove('is-visible');
        document.documentElement.classList.remove('r13-mobile-sticky-atc-is-visible');
      })
      .catch((error) => {
        console.error(error);
        this.submitText.textContent = 'Try again';
      })
      .finally(() => {
        this.setAddingState(false);
      });
  }

  setAddingState(isAdding) {
    this.isAdding = isAdding;
    this.submitButton?.classList.toggle('loading', isAdding);
    if (this.submitButton) this.submitButton.disabled = isAdding;
    if (isAdding && this.submitText) this.submitText.textContent = 'Adding';
    if (!isAdding) this.updateButtonStateFromMainButton();
  }

  handleMainSubmitClick(event) {
    if (this.skipNextMainSubmitClick) {
      this.skipNextMainSubmitClick = false;
      return;
    }

    if (!this.shouldUseMobileSizePicker()) return;
    if (this.isMainPurchaseAreaVisible()) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    if (this.hasPickedSizeFromTray) {
      this.submitMainForm({ bypassPicker: true });
      return;
    }

    this.openSizePicker();
  }

  handleMobileFocusButtonClick(event) {
    if (!event.target.closest('[data-r13-mobile-focus-atc-button]')) return;
    if (!this.shouldUseMobileSizePicker()) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    if (this.hasPickedSizeFromTray) {
      this.submitMainForm({ bypassPicker: true });
      return;
    }

    this.openSizePicker();
  }

  handleCartUpdate(event) {
    if (event?.cartData?.items) {
      this.applyPreferredSizeFromCart(event.cartData);
      return;
    }

    if (Number(event?.cartData?.item_count) !== 0) {
      this.syncPreferredSizeFromCart();
      return;
    }

    this.clearCartPreferredSize();
  }

  observeCartDrawer() {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer || typeof MutationObserver === 'undefined') return;

    this.cartDrawerObserver = new MutationObserver(this.boundCartDrawerMutation);
    this.cartDrawerObserver.observe(drawer, {
      attributes: true,
      attributeFilter: ['class'],
      childList: true,
      subtree: true,
    });
  }

  handleCartDrawerMutation() {
    const drawer = document.querySelector('cart-drawer');
    if (!drawer) return;

    if (!this.dismissedAfterAdd) {
      this.updateVisibility();
      return;
    }

    if (drawer.classList.contains('active') && !drawer.classList.contains('is-empty')) return;

    this.dismissedAfterAdd = false;
    this.restorePreferredSizeSelection();
    this.updateVisibility();
  }

  shouldUseMobileSizePicker() {
    return window.matchMedia('(max-width: 749px)').matches && Boolean(this.sizePanel && this.sizeOptionPosition);
  }

  openSizePicker() {
    if (!this.shouldUseMobileSizePicker()) return;

    this.dismissedAfterAdd = false;
    this.sizePickerOpen = true;
    if (!this.restorePreferredSizeSelection()) {
      this.hasPickedSizeFromTray = false;
      this.selectedSize = '';
      this.selectedSizeSource = '';
    }
    this.sizePickerOpenScrollY = window.scrollY || window.pageYOffset || 0;
    this.classList.add('is-visible', 'is-picker-open');
    this.sizePanel?.removeAttribute('hidden');
    this.sizeToggle?.setAttribute('aria-expanded', 'true');
    document.documentElement.classList.add('r13-mobile-sticky-atc-is-visible', 'r13-mobile-size-picker-is-open');
    this.updateSelectedSize();
    this.updateButtonStateFromMainButton();
  }

  closeSizePicker(options = {}) {
    this.sizePickerOpen = false;
    this.classList.remove('is-picker-open');
    this.sizePanel?.setAttribute('hidden', '');
    this.sizeToggle?.setAttribute('aria-expanded', 'false');
    document.documentElement.classList.remove('r13-mobile-size-picker-is-open');
    if (!options.keepPickedSize) this.hasPickedSizeFromTray = false;
    if (!options.skipVisibilityUpdate) this.updateVisibility();
    this.updateButtonStateFromMainButton();
  }

  observeMainSubmitButton() {
    if (!this.mainSubmitButton) return;

    this.submitObserver = new MutationObserver(() => {
      this.updateButtonStateFromMainButton();
    });
    this.submitObserver.observe(this.mainSubmitButton, {
      attributes: true,
      attributeFilter: ['disabled', 'aria-disabled', 'class'],
      childList: true,
      subtree: true,
    });
    this.updateButtonStateFromMainButton();
  }

  setupVisibilityTracking() {
    this.visibilityHandler = this.updateVisibility.bind(this);

    if ('IntersectionObserver' in window) {
      this.formObserver = new IntersectionObserver(this.visibilityHandler, { threshold: 0.01 });
      const target = this.getMainSubmitVisibilityTarget();
      if (target) this.formObserver.observe(target);

      const footer = this.getFooter();
      if (footer) {
        this.footerObserver = new IntersectionObserver((entries) => {
          this.footerIsVisible = entries.some((entry) => entry.isIntersecting);
          this.updateVisibility();
        });
        this.footerObserver.observe(footer);
      }
    }

    window.addEventListener('scroll', this.visibilityHandler, { passive: true });
    window.addEventListener('resize', this.visibilityHandler);
    this.updateVisibility();
  }

  updateVisibility() {
    if (window.matchMedia('(min-width: 750px)').matches) {
      this.classList.remove('is-visible');
      document.documentElement.classList.remove('r13-mobile-sticky-atc-is-visible');
      this.closeSizePicker({ skipVisibilityUpdate: true });
      return;
    }

    if (this.dismissedAfterAdd) {
      this.classList.remove('is-visible');
      document.documentElement.classList.remove('r13-mobile-sticky-atc-is-visible');
      if (this.sizePickerOpen) this.closeSizePicker({ skipVisibilityUpdate: true });
      return;
    }

    if (this.isCartDrawerOpen()) {
      this.classList.remove('is-visible');
      document.documentElement.classList.remove('r13-mobile-sticky-atc-is-visible');
      if (this.sizePickerOpen) this.closeSizePicker({ skipVisibilityUpdate: true });
      return;
    }

    if (this.sizePickerOpen) {
      const currentScrollY = window.scrollY || window.pageYOffset || 0;
      if (Math.abs(currentScrollY - this.sizePickerOpenScrollY) > 24) {
        this.closeSizePicker({ skipVisibilityUpdate: true });
        this.classList.remove('is-visible');
        document.documentElement.classList.remove('r13-mobile-sticky-atc-is-visible');
        return;
      }

      this.classList.add('is-visible');
      document.documentElement.classList.add('r13-mobile-sticky-atc-is-visible');
      return;
    }

    const target = this.getMainSubmitVisibilityTarget();
    const footerVisible = this.footerIsVisible || this.isFooterInViewport();
    const shouldShow = target ? !this.isElementInViewport(target) && !footerVisible : !footerVisible;

    this.classList.toggle('is-visible', shouldShow);
    document.documentElement.classList.toggle('r13-mobile-sticky-atc-is-visible', shouldShow);

    if (!shouldShow) {
      this.closeSizePicker({ skipVisibilityUpdate: true });
    }
  }

  getFooter() {
    if (!this.footer || !document.contains(this.footer)) {
      this.footer = document.querySelector(
        '.shopify-section-group-footer-group, #shopify-section-footer, footer.footer, footer'
      );
    }

    return this.footer;
  }

  isFooterInViewport() {
    const footer = this.getFooter();
    if (!footer) return false;

    return this.isElementInViewport(footer);
  }

  isCartDrawerOpen() {
    return Boolean(document.querySelector('cart-drawer.active'));
  }

  getMainSubmitVisibilityTarget() {
    this.mainSubmitButton = document.getElementById(this.submitButtonId);
    return this.mainSubmitButton?.closest('.product-form__buttons') || this.mainSubmitButton;
  }

  isMainPurchaseAreaVisible() {
    const target = this.getMainSubmitVisibilityTarget();
    return Boolean(target && this.isElementInViewport(target));
  }

  isElementInViewport(element) {
    const rect = element.getBoundingClientRect();
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth;

    return rect.width > 0
      && rect.height > 0
      && rect.bottom > 0
      && rect.top < viewportHeight
      && rect.right > 0
      && rect.left < viewportWidth;
  }

  hasElementScrolledPastViewport(element) {
    const rect = element.getBoundingClientRect();

    return rect.width > 0 && rect.height > 0 && rect.bottom <= 0;
  }

  preserveScrollPosition(scrollY) {
    this.clearScrollRestores();
    this.watchForManualScrollRestoreCancel();

    const restore = () => {
      if (Math.abs(window.scrollY - scrollY) > 1) {
        window.scrollTo(0, scrollY);
      }

      this.updateVisibility();
    };

    this.restoreScrollAnimationFrame = window.requestAnimationFrame(restore);
    this.restoreScrollTimeouts = [50, 150, 350, 700, 1200, 2200, 3600].map((delay) => window.setTimeout(restore, delay));
  }

  lockVariantPageScroll() {
    window.R13_STICKY_VARIANT_SCROLL_LOCK_UNTIL = Date.now() + 5000;
  }

  watchForManualScrollRestoreCancel() {
    if (typeof AbortController === 'undefined') return;

    this.scrollRestoreCancelController?.abort();
    this.scrollRestoreCancelController = new AbortController();

    const cancelRestore = () => this.clearScrollRestores();
    const options = {
      passive: true,
      signal: this.scrollRestoreCancelController.signal,
    };

    window.addEventListener('touchstart', cancelRestore, options);
    window.addEventListener('touchmove', cancelRestore, options);
    window.addEventListener('pointerdown', cancelRestore, options);
    window.addEventListener('wheel', cancelRestore, options);
    window.addEventListener('keydown', cancelRestore, options);
  }

  clearScrollRestores() {
    if (this.restoreScrollAnimationFrame) {
      window.cancelAnimationFrame(this.restoreScrollAnimationFrame);
      this.restoreScrollAnimationFrame = null;
    }

    this.restoreScrollTimeouts?.forEach((timeoutId) => window.clearTimeout(timeoutId));
    this.restoreScrollTimeouts = [];

    this.scrollRestoreCancelController?.abort();
    this.scrollRestoreCancelController = null;
  }

  updateFromCurrentForm() {
    const variantId = this.variantIdInput?.value || this.dataset.selectedVariantId;
    const variant = this.product?.variants?.find((item) => String(item.id) === String(variantId));
    if (variant) this.updateFromVariant(variant, { syncSize: false });
  }

  syncPreferredSizeFromCart() {
    fetch(`${window.routes?.cart_url || '/cart'}.js`)
      .then((response) => response.ok ? response.json() : null)
      .then((cartData) => {
        if (cartData) this.applyPreferredSizeFromCart(cartData);
      })
      .catch(() => {});
  }

  applyPreferredSizeFromCart(cartData) {
    if (!cartData?.items?.length) {
      this.clearCartPreferredSize();
      return false;
    }

    const preferredSize = this.findPreferredCartSize(cartData.items);
    if (!preferredSize) {
      if (this.selectedSizeSource === 'cart') this.clearCartPreferredSize();
      return false;
    }

    this.cartPreferredSize = preferredSize;
    if (!this.hasPickedSizeFromTray || this.selectedSizeSource === 'cart') {
      this.preselectSize(preferredSize);
    }

    return true;
  }

  findPreferredCartSize(items) {
    const currentProductId = this.product?.id;
    const currentProductItem = items.find((item) => (
      String(item.product_id) === String(currentProductId) && this.isSelectableSize(this.getCartItemSize(item))
    ));
    return currentProductItem ? this.getCartItemSize(currentProductItem) : '';
  }

  getCartItemSize(item) {
    const sizeOption = item?.options_with_values?.find((option) => (
      String(option.name || '').toLowerCase().includes('size')
    ));
    if (sizeOption?.value) return sizeOption.value;

    if (this.sizeOptionPosition && item?.variant_options?.[this.sizeOptionPosition - 1]) {
      return item.variant_options[this.sizeOptionPosition - 1];
    }

    return '';
  }

  isSelectableSize(size) {
    if (!size) return false;

    const button = Array.from(this.querySelectorAll('[data-r13-sticky-size-option]')).find((option) => (
      option.dataset.r13StickySizeOption === size
    ));

    return Boolean(button && button.getAttribute('aria-disabled') !== 'true');
  }

  preselectSize(size) {
    if (!this.isSelectableSize(size)) return false;

    const variant = this.findVariantForSize(size);
    this.selectedSize = size;
    this.selectedSizeSource = 'cart';
    this.hasPickedSizeFromTray = true;
    if (variant) {
      this.dataset.selectedVariantId = variant.id;
      this.selectedVariantAvailable = Boolean(variant.available);
      if (this.variantIdInput) {
        this.variantIdInput.value = variant.id;
        this.variantIdInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
      this.updatePrice(variant.price);
      this.updateAvailability(variant.available);
    }

    this.updateSelectedSize();
    this.updateButtonStateFromMainButton();
    return true;
  }

  restorePreferredSizeSelection() {
    if (this.cartPreferredSize && this.preselectSize(this.cartPreferredSize)) return true;

    this.hasPickedSizeFromTray = false;
    this.selectedSize = '';
    this.selectedSizeSource = '';
    this.updateSelectedSize();
    this.updateButtonStateFromMainButton();
    return false;
  }

  clearCartPreferredSize() {
    this.cartPreferredSize = '';
    this.dismissedAfterAdd = false;
    this.hasPickedSizeFromTray = false;
    this.selectedSize = '';
    this.selectedSizeSource = '';
    this.updateSelectedSize();
    this.updateButtonStateFromMainButton();
    this.updateVisibility();
  }

  updateFromVariant(variant, options = {}) {
    if (!variant) return;

    this.dataset.selectedVariantId = variant.id;
    this.selectedVariantAvailable = Boolean(variant.available);
    const selectedSize = this.sizeOptionPosition ? variant[`option${this.sizeOptionPosition}`] : '';
    if (selectedSize && options.syncSize) this.selectedSize = selectedSize;

    this.updateSelectedSize();
    this.updatePrice(variant.price);
    this.updateAvailability(variant.available);
  }

  updateSelectedSize() {
    if (this.sizeText) this.sizeText.textContent = this.selectedSize;

    this.querySelectorAll('[data-r13-sticky-size-option]').forEach((button) => {
      button.setAttribute('aria-pressed', button.dataset.r13StickySizeOption === this.selectedSize ? 'true' : 'false');
    });
  }

  updatePrice(cents) {
    if (!this.priceText) return;
    this.priceText.textContent = this.formatMoney(cents);
  }

  updateAvailability(isAvailable) {
    if (!this.submitButton || !this.submitText) return;

    this.submitButton.disabled = !isAvailable;
    if (this.sizePickerOpen && !this.hasPickedSizeFromTray) {
      this.submitText.textContent = 'SELECT SIZE';
      if (this.priceText) this.priceText.hidden = true;
      if (this.priceSeparator) this.priceSeparator.hidden = true;
      return;
    }

    if (this.hasPickedSizeFromTray) {
      this.submitText.textContent = isAvailable ? 'ADD TO CART' : 'SOLD OUT';
      if (this.priceText) this.priceText.hidden = false;
      if (this.priceSeparator) this.priceSeparator.hidden = false;
      return;
    }

    this.submitText.textContent = isAvailable ? 'ADD TO CART' : 'SOLD OUT';
  }

  updateButtonStateFromMainButton() {
    if (!this.mainSubmitButton || !this.submitButton) return;

    const mainDisabled = this.mainSubmitButton.disabled || this.mainSubmitButton.getAttribute('aria-disabled') === 'true';
    const mainText = this.mainSubmitButton.querySelector('span')?.textContent?.trim();

    this.submitButton.disabled = this.hasPickedSizeFromTray ? !this.selectedVariantAvailable : mainDisabled;
    if (this.sizePickerOpen && !this.hasPickedSizeFromTray && this.submitText) {
      this.submitText.textContent = 'SELECT SIZE';
      if (this.priceText) this.priceText.hidden = true;
      if (this.priceSeparator) this.priceSeparator.hidden = true;
      return;
    }

    if (this.hasPickedSizeFromTray && this.submitText) {
      this.submitText.textContent = 'ADD TO CART';
      if (this.priceText) this.priceText.hidden = false;
      if (this.priceSeparator) this.priceSeparator.hidden = false;
      return;
    }

    if (mainText && this.submitText) this.submitText.textContent = mainText;
    if (this.priceText) this.priceText.hidden = false;
    if (this.priceSeparator) this.priceSeparator.hidden = false;
  }

  formatMoney(cents) {
    const value = Number(cents || 0) / 100;
    return this.moneyFormat
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/, Math.round(value).toLocaleString())
      .replace(/\{\{\s*amount\s*\}\}/, value.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }));
  }

  cssEscape(value) {
    if (window.CSS?.escape) return CSS.escape(value);
    return String(value).replace(/"/g, '\\"');
  }
}

if (!customElements.get('r13-mobile-sticky-atc')) {
  customElements.define('r13-mobile-sticky-atc', R13MobileStickyAtc);
}
